import { useId, useRef, useState, type FormEvent } from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  type CognitoUserSession,
  type IAuthenticationCallback,
  type CognitoUserPool,
} from 'amazon-cognito-identity-js';
import type { SelfSignupRequest } from '@quality-vet/contracts';
import { ModalCloseButton } from '@quality-vet/ui/modal-close-button';

import { PasswordInput } from '../components/password-input';
import { ServiceTermsAction, ServiceTermsModal } from '../components/floating-actions';
import { useDialogFocusTrap } from '../components/use-dialog-focus-trap';
import { cognitoError } from '../hooks/use-session';
import { signupClientRequestId } from '../signup-request-id';
import {
  completeAuthenticatorMfaChallenge,
  completeEmailMfaChallenge,
  selectMfaChallenge,
} from '../cognito-user-api';

type LoginMode =
  | 'login'
  | 'signup'
  | 'forgot-password'
  | 'reset-password'
  | 'new-password'
  | 'mfa'
  | 'mfa-selection';

type AuthenticationOutcome =
  | { kind: 'authenticated'; session: CognitoUserSession }
  | {
      kind: 'new-password';
      user: CognitoUser;
      attributes: Record<string, string>;
    }
  | {
      kind: 'mfa';
      user: CognitoUser;
      challengeName: string;
      challengeSession?: string;
      username?: string;
    }
  | {
      kind: 'mfa-selection';
      user: CognitoUser;
      challengeSession: string;
      username: string;
      methods: Array<'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP'>;
    };

type CognitoAuthenticationResponse = {
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
};

type CognitoUserWithChallengeInternals = CognitoUser & {
  authenticateUserInternal: (
    response: CognitoAuthenticationResponse,
    authenticationHelper: unknown,
    callback: IAuthenticationCallback,
  ) => unknown;
};

function selectableMfaMethods(
  challengeParameters: Record<string, string> | undefined,
): Array<'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP'> {
  const parameters = Object.values(challengeParameters ?? {}).join(' ');
  const methods: Array<'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP'> = [];
  if (parameters.includes('SOFTWARE_TOKEN_MFA')) methods.push('SOFTWARE_TOKEN_MFA');
  if (parameters.includes('EMAIL_OTP') || parameters.includes('EMAIL_MFA'))
    methods.push('EMAIL_OTP');
  return methods.length > 0 ? methods : ['SOFTWARE_TOKEN_MFA', 'EMAIL_OTP'];
}

function authenticationCallbacks(
  user: CognitoUser,
  resolve: (outcome: AuthenticationOutcome) => void,
  reject: (error: unknown) => void,
): IAuthenticationCallback {
  const unsupported = (challenge: string) =>
    reject(
      new Error(`${challenge} is not supported by this sign-in form. Please contact support.`),
    );

  return {
    onSuccess: (session) => resolve({ kind: 'authenticated', session }),
    onFailure: reject,
    newPasswordRequired: (userAttributes, requiredAttributes) => {
      const availableAttributes = userAttributes as Record<string, string>;
      const requiredNames = Array.isArray(requiredAttributes)
        ? requiredAttributes.filter((name): name is string => typeof name === 'string')
        : [];
      const unsupportedNames = requiredNames.filter(
        (name) => typeof availableAttributes[name] !== 'string',
      );
      if (unsupportedNames.length > 0) {
        unsupported(`Required profile attributes (${unsupportedNames.join(', ')})`);
        return;
      }
      const attributes = Object.fromEntries(
        requiredNames.map((name) => [name, availableAttributes[name] ?? '']),
      );
      resolve({ kind: 'new-password', user, attributes });
    },
    mfaRequired: (challengeName) => {
      const name = String(challengeName);
      if (name !== 'SMS_MFA') {
        unsupported(`${name} MFA`);
        return;
      }
      resolve({ kind: 'mfa', user, challengeName: name });
    },
    totpRequired: (challengeName) => {
      const name = String(challengeName);
      if (name !== 'SOFTWARE_TOKEN_MFA') {
        unsupported(`${name} MFA`);
        return;
      }
      resolve({ kind: 'mfa', user, challengeName: name });
    },
    customChallenge: () => unsupported('Custom authentication'),
    mfaSetup: () => unsupported('Authenticator setup during login'),
    selectMFAType: () => unsupported('MFA method selection'),
  };
}

function authenticate(user: CognitoUser, username: string, password: string) {
  return new Promise<AuthenticationOutcome>((resolve, reject) => {
    // amazon-cognito-identity-js 6.x predates Cognito's EMAIL_OTP challenge. Preserve its SRP
    // implementation, but intercept the new challenge before the library treats it as a token
    // response. The code response itself is sent through Cognito's public JSON API below.
    const userWithInternals = user as CognitoUserWithChallengeInternals;
    const defaultChallengeHandler = userWithInternals.authenticateUserInternal.bind(user);
    userWithInternals.authenticateUserInternal = (response, helper, callback) => {
      const challengeName = response.ChallengeName;
      if (challengeName === 'SELECT_MFA_TYPE') {
        if (!response.Session) {
          reject(new Error('Cognito returned an invalid MFA method selection.'));
          return;
        }
        resolve({
          kind: 'mfa-selection',
          user,
          challengeSession: response.Session,
          username,
          methods: selectableMfaMethods(response.ChallengeParameters),
        });
        return;
      }
      if (challengeName === 'EMAIL_OTP' || challengeName === 'EMAIL_MFA') {
        if (!response.Session) {
          reject(new Error('Cognito returned an invalid email MFA challenge.'));
          return;
        }
        resolve({
          kind: 'mfa',
          user,
          challengeName,
          challengeSession: response.Session,
          username,
        });
        return;
      }
      return defaultChallengeHandler(response, helper, callback);
    };
    user.authenticateUser(
      new AuthenticationDetails({ Username: username, Password: password }),
      authenticationCallbacks(user, resolve, reject),
    );
  });
}

function completeNewPassword(
  challenge: Extract<AuthenticationOutcome, { kind: 'new-password' }>,
  password: string,
) {
  return new Promise<AuthenticationOutcome>((resolve, reject) =>
    challenge.user.completeNewPasswordChallenge(
      password,
      challenge.attributes,
      authenticationCallbacks(challenge.user, resolve, reject),
    ),
  );
}

async function completeMfa(
  challenge: Extract<AuthenticationOutcome, { kind: 'mfa' }>,
  code: string,
): Promise<AuthenticationOutcome> {
  if (challenge.challengeName === 'EMAIL_OTP' || challenge.challengeName === 'EMAIL_MFA') {
    if (!challenge.challengeSession || !challenge.username) {
      throw new Error('The email MFA challenge has expired. Start the login again.');
    }
    const config = window.QUALITY_VET_CONFIG;
    return {
      kind: 'authenticated',
      session: await completeEmailMfaChallenge({
        authority: config.cognitoAuthority,
        clientId: config.cognitoClientId,
        username: challenge.username,
        challengeName: challenge.challengeName,
        challengeSession: challenge.challengeSession,
        code,
      }),
    };
  }
  if (challenge.challengeSession && challenge.username) {
    const config = window.QUALITY_VET_CONFIG;
    return {
      kind: 'authenticated',
      session: await completeAuthenticatorMfaChallenge({
        authority: config.cognitoAuthority,
        clientId: config.cognitoClientId,
        username: challenge.username,
        challengeSession: challenge.challengeSession,
        code,
      }),
    };
  }
  return new Promise<AuthenticationOutcome>((resolve, reject) =>
    challenge.user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve({ kind: 'authenticated', session }),
        onFailure: reject,
      },
      challenge.challengeName,
    ),
  );
}

function initialLoginMode(signupEnabled: boolean): LoginMode {
  const requested = new URLSearchParams(window.location.search).get('auth');
  if (requested === 'signup' && signupEnabled) return requested;
  return requested === 'forgot-password' ? requested : 'login';
}

function titleFor(mode: LoginMode): string {
  if (mode === 'signup') return 'Create your account';
  if (mode === 'forgot-password') return 'Reset your password';
  if (mode === 'reset-password' || mode === 'new-password') return 'Choose a new password';
  if (mode === 'mfa') return 'Verify your identity';
  if (mode === 'mfa-selection') return 'Choose how to verify';
  return 'Login';
}

export function LoginPanel({
  apiUrl,
  configured,
  pool,
  publicUrl,
  signupEnabled,
  error,
  onLogin,
}: {
  apiUrl: string;
  configured: boolean;
  pool: CognitoUserPool | undefined;
  publicUrl: string;
  signupEnabled: boolean;
  error: string;
  onLogin: (session: CognitoUserSession) => Promise<void>;
}) {
  const [mode, setMode] = useState<LoginMode>(() => initialLoginMode(signupEnabled));
  const [email, setEmail] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [serviceTermsAccepted, setServiceTermsAccepted] = useState(false);
  const [serviceTermsOpen, setServiceTermsOpen] = useState(false);
  const serviceTermsConsentId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const authenticationChallenge = useRef<AuthenticationOutcome | null>(null);
  const closeLogin = () => window.location.assign(publicUrl);
  useDialogFocusTrap(dialogRef, closeLogin, !serviceTermsOpen, {
    isolationIgnoreSelector: '.brand, .floating-actions',
  });

  const setView = (next: LoginMode) => {
    if (next !== 'mfa' && next !== 'mfa-selection' && next !== 'new-password') {
      authenticationChallenge.current = null;
    }
    const currentUrl = new URL(window.location.href);
    if (next === 'signup') currentUrl.searchParams.set('auth', 'signup');
    else if (currentUrl.searchParams.get('auth') === 'signup')
      currentUrl.searchParams.delete('auth');
    window.history.replaceState({}, '', currentUrl);
    setMode(next);
    setPassword('');
    setConfirmation('');
    setCode('');
    setMessage('');
    setFormError('');
    setServiceTermsAccepted(false);
    setServiceTermsOpen(false);
  };
  const user = () => (pool ? new CognitoUser({ Username: email.trim(), Pool: pool }) : undefined);
  const applyAuthenticationOutcome = async (outcome: AuthenticationOutcome) => {
    if (outcome.kind === 'authenticated') {
      authenticationChallenge.current = null;
      await onLogin(outcome.session);
      return;
    }
    authenticationChallenge.current = outcome;
    setView(outcome.kind);
    setMessage(
      outcome.kind === 'mfa-selection'
        ? 'Choose an MFA method.'
        : outcome.kind === 'mfa'
          ? outcome.challengeName === 'EMAIL_OTP' || outcome.challengeName === 'EMAIL_MFA'
            ? 'Enter the six-digit verification code sent to your email address.'
            : 'Enter the six-digit code from your authenticator app.'
          : 'Choose a new password to finish signing in.',
    );
  };
  const chooseMfaMethod = async (method: 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP') => {
    const challenge = authenticationChallenge.current;
    if (!challenge || challenge.kind !== 'mfa-selection') {
      setFormError('The MFA method selection has expired. Start the login again.');
      return;
    }
    setPending(true);
    setMessage('');
    setFormError('');
    try {
      const config = window.QUALITY_VET_CONFIG;
      const nextChallenge = await selectMfaChallenge({
        authority: config.cognitoAuthority,
        clientId: config.cognitoClientId,
        username: challenge.username,
        challengeSession: challenge.challengeSession,
        method,
      });
      await applyAuthenticationOutcome({
        kind: 'mfa',
        user: challenge.user,
        username: challenge.username,
        ...nextChallenge,
      });
    } catch (selectionError) {
      setFormError(
        selectionError instanceof Error ? selectionError.message : 'MFA method selection failed.',
      );
    } finally {
      setPending(false);
    }
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    setFormError('');
    if (!pool) return;
    const username = email.trim();
    if (!username) {
      setFormError('Enter your email address.');
      return;
    }
    if (mode === 'signup' && !serviceTermsAccepted) {
      setFormError('You must agree to the Service Terms to sign up.');
      return;
    }
    const organisation = organisationName.trim();
    if (mode === 'signup' && !organisation) {
      setFormError('Enter your organisation name.');
      return;
    }
    if ((mode === 'reset-password' || mode === 'new-password') && password !== confirmation) {
      setFormError('Passwords do not match.');
      return;
    }
    setPending(true);
    try {
      if (mode === 'login') {
        const cognitoUser = user();
        if (!cognitoUser) throw new Error('Cognito is not configured.');
        await applyAuthenticationOutcome(await authenticate(cognitoUser, username, password));
        return;
      }
      if (mode === 'new-password') {
        const challenge = authenticationChallenge.current;
        if (!challenge || challenge.kind !== 'new-password') {
          throw new Error('The password challenge has expired. Start the login again.');
        }
        await applyAuthenticationOutcome(await completeNewPassword(challenge, password));
        return;
      }
      if (mode === 'mfa') {
        const challenge = authenticationChallenge.current;
        if (!challenge || challenge.kind !== 'mfa') {
          throw new Error('The MFA challenge has expired. Start the login again.');
        }
        if (!/^\d{6}$/.test(code.trim())) {
          throw new Error('Enter the six-digit verification code.');
        }
        await applyAuthenticationOutcome(await completeMfa(challenge, code.trim()));
        return;
      }
      if (mode === 'signup') {
        if (!signupEnabled) throw new Error('Signup is not available in this environment.');
        const { serviceTermsContentHash, serviceTermsVersion } =
          await import('@quality-vet/contracts/service-terms');
        const request: SelfSignupRequest = {
          email: username,
          organisationName: organisation,
          termsVersion: serviceTermsVersion,
          termsHash: serviceTermsContentHash,
          confirmed: true,
          clientRequestId: await signupClientRequestId(
            username,
            organisation,
            serviceTermsVersion,
            serviceTermsContentHash,
          ),
        };
        const response = await fetch(`${apiUrl}/v1/signups`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(request),
        });
        if (!response.ok) {
          throw new Error(
            response.status === 400
              ? 'Check the signup details and try again.'
              : response.status === 409
                ? 'The Service Terms changed. Reload this page, review them and try again.'
                : response.status === 429
                  ? 'Too many signup attempts. Please wait before trying again.'
                  : 'Your signup could not be completed. Please try again.',
          );
        }
        setView('login');
        setOrganisationName('');
        setMessage('If this address can be registered, check your email for next steps.');
        return;
      }
      if (mode === 'forgot-password') {
        const cognitoUser = user();
        if (!cognitoUser) throw new Error('Cognito is not configured.');
        await new Promise<void>((resolve) =>
          cognitoUser.forgotPassword({
            // Account-state errors are intentionally indistinguishable from a successful request.
            // This prevents the reset form from becoming an account-enumeration oracle.
            onSuccess: () => resolve(),
            onFailure: () => resolve(),
            inputVerificationCode: () => resolve(),
          }),
        );
        setView('reset-password');
        return;
      }
      await new Promise<void>((resolve, reject) =>
        user()?.confirmPassword(code.trim(), password, {
          onSuccess: () => resolve(),
          onFailure: (resetPasswordError) => reject(cognitoError(resetPasswordError)),
        }),
      );
      setView('login');
      setMessage('Your password has been reset. You can now log in.');
    } catch (submitError) {
      setFormError(submitError instanceof Error ? submitError.message : 'Something went wrong.');
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="login-page" data-auth-contract="quality-vet-auth-app-modal-v2">
      <a className="skip-link" href="#login-content">
        Skip to sign in
      </a>
      <a
        className="brand"
        href={publicUrl}
        aria-label="quality.vet home"
        onClick={() => window.location.assign(publicUrl)}
      >
        quality<span>.vet</span>
      </a>
      <section
        className="login-modal"
        role="dialog"
        aria-modal="true"
        aria-label={configured ? undefined : 'Sign in'}
        aria-labelledby="login-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        <ModalCloseButton
          className="app-modal-close login-modal-close"
          label="Close login"
          onClick={closeLogin}
        />
        {configured ? (
          <form
            className="login-form"
            id="login-content"
            tabIndex={-1}
            onSubmit={(event) => void submit(event)}
          >
            <h1 id="login-title" className="sr-only">
              {titleFor(mode)}
            </h1>
            {mode === 'login' || mode === 'signup' ? (
              <label>
                Email
                <input
                  autoFocus
                  autoComplete="email"
                  maxLength={254}
                  required
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
            ) : null}
            {mode === 'signup' ? (
              <label>
                Organisation name
                <input
                  autoComplete="organization"
                  maxLength={160}
                  minLength={2}
                  required
                  value={organisationName}
                  onChange={(event) => setOrganisationName(event.target.value)}
                />
              </label>
            ) : null}
            {mode === 'login' || mode === 'reset-password' || mode === 'new-password' ? (
              <div className="login-field">
                <label htmlFor="login-password">Password</label>
                <PasswordInput
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  id="login-password"
                  minLength={mode === 'login' ? undefined : 14}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
            ) : null}
            {mode === 'reset-password' || mode === 'new-password' ? (
              <div className="login-field">
                <label htmlFor="login-password-confirmation">Confirm password</label>
                <PasswordInput
                  autoComplete="new-password"
                  id="login-password-confirmation"
                  minLength={14}
                  required
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                />
              </div>
            ) : null}
            {mode === 'reset-password' || mode === 'mfa' ? (
              <label>
                {mode === 'mfa' ? 'MFA verification code' : 'Verification code'}
                <input
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={mode === 'mfa' ? 6 : undefined}
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </label>
            ) : null}
            {mode === 'signup' ? (
              <div className="signup-consent">
                <input
                  checked={serviceTermsAccepted}
                  id={serviceTermsConsentId}
                  onChange={(event) => setServiceTermsAccepted(event.target.checked)}
                  required
                  type="checkbox"
                />
                <span>
                  <label htmlFor={serviceTermsConsentId}>I agree to the</label>{' '}
                  <a
                    className="signup-consent-link text-link"
                    href="#service-terms"
                    onClick={(event) => {
                      event.preventDefault();
                      setServiceTermsOpen(true);
                    }}
                  >
                    Service Terms
                  </a>
                  .
                </span>
              </div>
            ) : null}
            {mode === 'forgot-password' ? (
              <label>
                Email
                <input
                  autoComplete="email"
                  required
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
            ) : null}
            {mode === 'mfa-selection' ? (
              <div className="mfa-login-methods">
                {authenticationChallenge.current?.kind === 'mfa-selection' &&
                authenticationChallenge.current.methods.includes('SOFTWARE_TOKEN_MFA') ? (
                  <button
                    className="login-submit"
                    disabled={pending}
                    type="button"
                    onClick={() => void chooseMfaMethod('SOFTWARE_TOKEN_MFA')}
                  >
                    Use authenticator app
                  </button>
                ) : null}
                {authenticationChallenge.current?.kind === 'mfa-selection' &&
                authenticationChallenge.current.methods.includes('EMAIL_OTP') ? (
                  <button
                    className="login-submit"
                    disabled={pending}
                    type="button"
                    onClick={() => void chooseMfaMethod('EMAIL_OTP')}
                  >
                    Use email code
                  </button>
                ) : null}
              </div>
            ) : (
              <button className="login-submit" disabled={pending} type="submit">
                {pending
                  ? 'Please wait...'
                  : mode === 'login'
                    ? 'Login'
                    : mode === 'signup'
                      ? 'Signup'
                      : mode === 'forgot-password'
                        ? 'Send code'
                        : mode === 'reset-password'
                          ? 'Reset password'
                          : mode === 'new-password'
                            ? 'Set password and login'
                            : 'Verify and login'}
              </button>
            )}
            {mode === 'login' ? (
              <div className={`login-links${signupEnabled ? '' : ' login-links-single'}`}>
                {signupEnabled ? (
                  <button type="button" onClick={() => setView('signup')}>
                    Signup
                  </button>
                ) : null}
                <button type="button" onClick={() => setView('forgot-password')}>
                  Forgot password
                </button>
              </div>
            ) : (
              <button className="login-back" type="button" onClick={() => setView('login')}>
                Login
              </button>
            )}
            {message ? (
              <p aria-atomic="true" aria-live="polite" className="login-message" role="status">
                {message}
              </p>
            ) : null}
            {formError ? (
              <p className="auth-error" role="alert">
                {formError}
              </p>
            ) : null}
          </form>
        ) : (
          <p className="auth-error" role="alert">
            Cognito has not been configured for this environment. Please contact quality.vet
            support.
          </p>
        )}
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
      </section>
      <aside aria-label="Legal information" className="floating-actions">
        <ServiceTermsAction expanded={serviceTermsOpen} onClick={() => setServiceTermsOpen(true)} />
      </aside>
      <ServiceTermsModal onClose={() => setServiceTermsOpen(false)} open={serviceTermsOpen} />
    </div>
  );
}
